// Reduce el peso de una foto antes de subirla: la achica (lado mayor máx. `maxLado` px) y la guarda como WebP.
// Un celular entrega fotos de 3-8 MB; con esto quedan en unos 100-400 KB sin que se note la diferencia en pantalla.
// GIF (animado) y archivos que no son imagen se devuelven tal cual. Si el resultado no pesa menos, se conserva el original.
const TIPOS_COMPRIMIBLES = ["image/jpeg", "image/png", "image/webp"];

const cargarBitmap = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" }); // respeta la rotación de la cámara
    } catch (e) {
      // se intenta con <img>
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("No se pudo leer la imagen"));
      el.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
};

const aBlob = (canvas, tipo, calidad) => new Promise((resolve) => canvas.toBlob(resolve, tipo, calidad));

export const formatearPeso = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Devuelve { file, original, final, reducida }.
export async function comprimirImagen(file, { maxLado = 1600, calidad = 0.8 } = {}) {
  const sinCambios = { file, original: file.size, final: file.size, reducida: false };
  if (!TIPOS_COMPRIMIBLES.includes(file.type)) return sinCambios;
  try {
    const bitmap = await cargarBitmap(file);
    const ancho = bitmap.width || bitmap.naturalWidth;
    const alto = bitmap.height || bitmap.naturalHeight;
    const escala = Math.min(1, maxLado / Math.max(ancho, alto));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(ancho * escala));
    canvas.height = Math.max(1, Math.round(alto * escala));
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (bitmap.close) bitmap.close();

    let blob = await aBlob(canvas, "image/webp", calidad);
    if (!blob || blob.type !== "image/webp") blob = await aBlob(canvas, "image/jpeg", calidad); // navegadores sin WebP
    if (!blob || (blob.size >= file.size && escala === 1)) return sinCambios;

    const ext = blob.type === "image/webp" ? ".webp" : ".jpg";
    const nombre = `${(file.name || "imagen").replace(/\.[^.]+$/, "")}${ext}`;
    return { file: new File([blob], nombre, { type: blob.type }), original: file.size, final: blob.size, reducida: true };
  } catch (e) {
    return sinCambios; // si algo falla, se sube la original en vez de bloquear al usuario
  }
}
